import { AxiosError } from 'axios'
import i18n from '@app/i18n'
import store from '@store/configureStore'
import { DocumentMode } from '@store/modules/document/types'
import documentsService from '@services/documents'

export const coeditBusyError = (): Error => Object.assign(new Error(i18n.t('document:coedit.actionBlocked')), {
  response: { status: 409, data: { message: i18n.t('document:coedit.actionBlocked') } }
})

// The fresh read covers stale lists and dialogs opened before another user started coedit.
// The backend must still reject races between this check and the actual mutation.
export const withIdleCoeditDocument = async <T>(documentOguids: string[], action: () => Promise<T>, orgOguid?: string): Promise<T> => {
  const initial = store.getState()
  const organization = orgOguid ?? initial.user.activeOrganization.oguid
  const user = initial.user.profile.oguid
  const pathname = window.location.pathname
  const current = initial.documents.currentDocument.oguid
  const sameContext = () => {
    const state = store.getState()
    return state.user.profile.oguid === user && state.user.activeOrganization.oguid === initial.user.activeOrganization.oguid &&
      window.location.pathname === pathname
  }
  try {
    for (const documentOguid of Array.from(new Set(documentOguids))) {
      const { data } = await documentsService.getDocumentData(documentOguid, { params: { _coedit: Date.now() } }, organization)
      if (!sameContext()) throw new Error(i18n.t('document:coedit.requestFailed'))
      if (data.isCoediting === true) throw coeditBusyError()
    }
    if (!sameContext()) throw new Error(i18n.t('document:coedit.requestFailed'))
    return await action()
  } catch (error) {
    if ((error as AxiosError).response?.status === 409 && sameContext()) {
      // Preserve rejection so operation counters and dialogs cannot report success.
      const { getDocumentData } = await import('@store/modules/documents/actions')
      const shouldApply = () => sameContext() && store.getState().user.activeOrganization.oguid === organization &&
        store.getState().documents.currentDocument.oguid === current && store.getState().document.mode === DocumentMode.VIEW
      if (current && shouldApply()) {
        await (store.dispatch as any)(getDocumentData(current, false, shouldApply, true)).catch(() => undefined)
      }
      const response = (error as AxiosError<{ message?: string }>).response
      if (response && !response.data?.message) response.data = { message: i18n.t('document:coedit.actionBlocked') }
    }
    throw error
  }
}

export const getAttachmentDocumentOguid = (attachmentOguid?: string): string | undefined => {
  if (!attachmentOguid) return undefined
  const state = store.getState()
  const organization = state.user.activeOrganization.oguid
  const documents = [
    state.documents.currentDocument,
    ...state.documents.documents,
    ...state.documents.messages.flatMap(message => message.documents),
    ...state.tasks.messages.filter(message => !message.org || message.org.oguid === organization).map(message => message.document)
  ]
  return documents.find(document => document.documentAttachmentOguid === attachmentOguid)?.oguid ??
    state.receipts.data.find(receipt => receipt.attachmentOguid === attachmentOguid)?.documentOguid
}
